import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import Corners from "../ui/Corners";
import { ArrowRight } from "../ui/Icons";
import { scrollToSection } from "../../lib/scroll";
import "./Hero.css";

/* ---------- simulated detection log (demo feed) ---------- */
const CLASSES = [
  { cls: "car", tone: "acc", w: 0.58 },
  { cls: "truck", tone: "amb", w: 0.12 },
  { cls: "bus", tone: "amb", w: 0.06 },
  { cls: "person", tone: "cyan", w: 0.17 },
  { cls: "bicycle", tone: "cyan", w: 0.07 },
];

function pickClass() {
  let r = Math.random();
  for (const c of CLASSES) {
    if ((r -= c.w) <= 0) return c;
  }
  return CLASSES[0];
}

function makeEvent(seed) {
  const c = pickClass();
  const d = new Date();
  const t =
    d.toTimeString().slice(0, 8) +
    "." +
    String(d.getMilliseconds()).padStart(3, "0");
  return {
    key: seed,
    t,
    id: String(400 + ((seed * 37) % 560)).padStart(4, "0"),
    cls: c.cls,
    tone: c.tone,
    conf: (0.72 + Math.random() * 0.27).toFixed(2),
    zone: `lane ${1 + (seed % 4)}`,
  };
}

function DetectionLog() {
  const reduce = useReducedMotion();
  const [rows, setRows] = useState(() =>
    Array.from({ length: 6 }, (_, i) => makeEvent(i + 1)),
  );
  const seed = useRef(7);

  useEffect(() => {
    if (reduce) return undefined;
    let timer;
    const tick = () => {
      if (!document.hidden) {
        seed.current += 1;
        setRows((r) => [...r.slice(-5), makeEvent(seed.current)]);
      }
      timer = setTimeout(tick, 650 + Math.random() * 700);
    };
    timer = setTimeout(tick, 900);
    return () => clearTimeout(timer);
  }, [reduce]);

  return (
    <div className="log" aria-hidden="true">
      <div className="log__head">
        <span className="dot" /> detections
      </div>
      <ul className="log__rows">
        {rows.map((r) => (
          <li key={r.key} className="log__row">
            <span className="log__t">{r.t}</span>
            <span className="log__id">#{r.id}</span>
            <span className={`log__cls log__cls--${r.tone}`}>{r.cls}</span>
            <span className="log__conf">{r.conf}</span>
            <span className="log__zone">{r.zone}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- animated counter for the stat tiles ---------- */
function useJitter(base, spread, every = 1800) {
  const reduce = useReducedMotion();
  const [v, setV] = useState(base);
  useEffect(() => {
    if (reduce) return undefined;
    const id = setInterval(() => {
      if (document.hidden) return;
      setV(base + Math.round((Math.random() - 0.5) * spread));
    }, every);
    return () => clearInterval(id);
  }, [base, spread, every, reduce]);
  return v;
}

/* ---------- viewfinder console ---------- */
function Viewfinder() {
  const videoRef = useRef(null);
  const objects = useJitter(37, 10);
  const latency = useJitter(18, 6, 2400);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.1 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <div className="console">
      <div className="vf">
        <video
          ref={videoRef}
          className="vf__video"
          src="/home/hero.mp4"
          poster="/home/hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
        <div className="vf__vignette" />
        <div className="vf__scan" />
        <Corners />
        <div className="vf__hud">
          <div className="vf__row">
            <span className="vf__tag">
              <span className="vf__rec" /> CAM-04 · intersection-n
            </span>
            <span className="vf__tag vf__tag--demo">demo feed</span>
            <span className="vf__tag vf__tag--right">24 fps · 1024×640</span>
          </div>
          <div className="vf__row vf__row--bottom">
            <span className="vf__tag vf__tag--right vf__tag--model">
              yolo-v8m · int8 · orin nx
            </span>
          </div>
        </div>
        <DetectionLog />
      </div>

      <ul className="stats" aria-label="Simulated live statistics">
        <li className="stat">
          <span className="stat__label">Objects tracked</span>
          <span className="stat__value">{objects}</span>
        </li>
        <li className="stat">
          <span className="stat__label">Edge latency</span>
          <span className="stat__value">
            {latency}
            <small>ms</small>
          </span>
        </li>
        <li className="stat">
          <span className="stat__label">Supervision</span>
          <span className="stat__value">
            24<small>/7</small>
          </span>
        </li>
      </ul>
    </div>
  );
}

/* ---------- hero ---------- */
export default function Hero() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  const item = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.2, 0.7, 0.2, 1], delay },
  });

  return (
    <section id="home" className="hero">
      <div className="hero__glow" aria-hidden="true" />
      <div className="container hero__grid">
        <div className="hero__copy">
          <motion.span className="eyebrow" {...item(0.05)}>
            Live · Enterprise vision intelligence
          </motion.span>
          <motion.h1 className="display" {...item(0.15)}>
            Vision intelligence for the <em>physical world</em>.
          </motion.h1>
          <motion.p className="lead" {...item(0.28)}>
            Episcope AI turns the cameras you already own into 24/7 intelligent
            observers: detecting hazards, tracking assets and surfacing insights
            in real time for industrial, urban and security operations. On the
            edge, in the cloud, or both.
          </motion.p>
          <motion.div className="hero__cta" {...item(0.4)}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => scrollToSection("contact")}
            >
              Book a demo <ArrowRight className="i" />
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => navigate("/vision-platform")}
            >
              Explore the platform
            </button>
          </motion.div>
          <motion.ul className="hero__meta" {...item(0.5)}>
            <li>
              <b>Ingest</b> RTSP · ONVIF · WebRTC
            </li>
            <li>
              <b>Runs on</b> Jetson · x86 · cloud GPU
            </li>
            <li>
              <b>Deploy</b> Edge · Cloud · Hybrid
            </li>
          </motion.ul>
        </div>

        <motion.div
          className="hero__console"
          initial={reduce ? false : { opacity: 0, y: 30, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, ease: [0.2, 0.7, 0.2, 1], delay: 0.3 }}
        >
          <Viewfinder />
        </motion.div>
      </div>
    </section>
  );
}
